'use client';

import { skipToken, useQuery } from '@tanstack/react-query';

export const PLUGIN_AUTH_STATE_KEY = '@plugin-ui/PLUGIN_AUTH_STATE';

const POLL_INTERVAL_MS = 5000;

export const getPluginAuthStateKey = (params?: UsePluginAuthStateParams) => {
  return [PLUGIN_AUTH_STATE_KEY, params];
};

/** Air plugins that use the `/pluginAuth/state` OAuth handshake. */
export type PluginName = 'contentful' | 'figma' | 'premiere' | 'sanity' | 'shopify' | 'wordpress';

/**
 * `GET /pluginAuth/state` response. Mirrors the backend `GetPluginAuthStateResponse`
 * (server-sdk `PluginAuth.ts`); not re-exported from `@air/api`, so typed locally to avoid
 * pulling the whole api client into this package.
 */
export type PluginAuthState = {
  code: string;
  state: string;
  status: 'authorized';
};

export type UsePluginAuthStateParams = {
  apiEndpoint: string;
  /** Unique identifier for this auth session */
  id: string | undefined;
  pluginName: PluginName;
};

export const usePluginAuthState = ({ apiEndpoint, id, pluginName }: UsePluginAuthStateParams) => {
  return useQuery({
    queryFn: id
      ? async (): Promise<PluginAuthState> => {
          const response = await fetch(`${apiEndpoint}/pluginAuth/state?id=${id}&pluginName=${pluginName}`);

          if (!response.ok) {
            throw new Error(`Failed to fetch auth state: ${response.status} ${response.statusText}`);
          }

          return response.json();
        }
      : skipToken,
    queryKey: getPluginAuthStateKey({ apiEndpoint, id, pluginName }),
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
  });
};
